/**
 * The four bands a page can close on. `variant` names two of them; the other
 * two are told apart by what the section carries, since the data states no
 * value for either:
 *
 * - `plain`: `.table_button_wrap`, the tinted well around a register control.
 * - `alternative`: `.alter-cta_wrap`, the comparison pages' mint-lit panel.
 * - `gradient`: `.y-p_cta_box`, the pastel band the YouTube page closes on.
 * - otherwise: `.cta_wrap`, the deep navy band the earn pages close on.
 */
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import MarketingLottie from "@/components/marketing/MarketingLottie";
import { BODY, HEADING_2 } from "@/components/marketing/marketingType";
import {
  FAQ_RAIL,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import type { LandingCta } from "@/content/landing/types";
import {
  LandingButton,
  LandingSection,
  ReassuranceRow,
} from "../LandingPrimitives";
import {
  PlainBandHeading,
  CheckRow,
  FIXED_BUTTON_ROW,
  SHALLOW_FOOT,
} from "./landingBands";
import { Lines } from "@/components/marketing/Lines";

type CtaBand = Laid<"ctaBand">;

const BAND_FRAME = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: "20px",
} satisfies SxProps<Theme>;

/**
 * `.cta_wrap`: the dark band the referral and affiliate pages close on. The
 * deep foot padding below the card step clears artwork the published band draws.
 */
const DARK_BAND = {
  gap: "40px",
  justifyContent: "center",
  height: "400px",
  p: "80px 100px",
  borderRadius: "48px",
  backgroundColor: marketingPalette.darkGround,
  position: "relative",
  overflow: "hidden",
  [NOT_WIDE]: { height: "auto", p: "80px 96px" },
  [MID]: { p: "32px 24px 144px", borderRadius: "24px" },
  [TINY]: { p: "48px 16px 192px" },
};

/** `.alter-cta_wrap`: the comparison pages' band, on a mint inset glow. */
const ALT_BAND = {
  gap: "40px",
  p: "64px",
  borderRadius: "24px",
  backgroundColor: marketingPalette.darkGround,
  position: "relative",
  overflow: "hidden",
  boxShadow: "inset 0 0 93px rgba(26, 179, 148, 0.5)",
  [NOT_WIDE]: { p: "32px 48px", borderRadius: "32px" },
  [MID]: { p: "64px 32px", borderRadius: "24px" },
  [TINY]: { p: "48px 32px" },
};

/**
 * `.y-p_cta_box`: the pastel band, blue through lilac to pink. Its heading and
 * line are the page's own ink, not a dark band's white.
 */
const GRADIENT_BAND = {
  gap: "24px",
  justifyContent: "center",
  p: "48px",
  borderRadius: "32px",
  backgroundImage: "linear-gradient(255deg, #E7F2FF, #EBEFFF 52%, #FEE3FF)",
  overflow: "hidden",
  [NOT_WIDE]: { p: "32px 32px 48px" },
  [MID]: { p: "32px 16px 48px", borderRadius: "16px" },
};

/**
 * `.table_button_wrap`: the tinted well the product pages set their register
 * control in, on the FAQ's own rail rather than the section's.
 */
const PLAIN_WELL = {
  gap: "8px",
  justifyContent: "center",
  width: 1,
  maxWidth: FAQ_RAIL,
  mx: "auto",
  p: "16px",
  borderRadius: "16px",
  backgroundColor: marketingPalette.surface,
  // `.button_new.is_hug`: hugged at every width, where a band's control fills.
  "& a, & button": { [MID]: { width: "auto" } },
};

/** `.cta-small_title_wrap`: heading, line and control as one stack. */
const DARK_INFO = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  [MID]: { width: 1 },
};

/** `.alter-cta_info-wrap`: the same pair on its own measure. */
const ALT_INFO = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: 1,
  maxWidth: "650px",
};

/**
 * `.cta_title`: the page's `h2` on its own ladder, not the section size. Not
 * `.h2_new`, so its lines break where they fill rather than balanced.
 */
const DARK_TITLE = {
  color: marketingPalette.surface,
  fontSize: 38,
  lineHeight: "48px",
  fontWeight: 600,
  letterSpacing: "-0.04em",
  textWrap: "wrap",
  [NOT_WIDE]: { fontSize: 44, lineHeight: "50px" },
  [MID]: { fontSize: 37 },
  [TINY]: { fontSize: 28, lineHeight: "33.6px" },
};

/** `h1-new.is-white-txt`: this one band states its heading at hero size, unbalanced. */
const ALT_TITLE = {
  color: "common.white",
  fontSize: 52,
  lineHeight: "57.2px",
  letterSpacing: "-0.04em",
  textWrap: "wrap",
  [NOT_WIDE]: { fontSize: 48, lineHeight: "54px" },
  [MID]: { fontSize: 40, lineHeight: "52px" },
  [TINY]: { fontSize: 34, lineHeight: "40.8px" },
};

/** The same title published on a `div`, which the site's `h2` rule does not track. */
const ALT_TITLE_DIV = { ...ALT_TITLE, letterSpacing: "normal" };

/** `.cta-small_body`: forty characters of it, held at 80%, its lines centred as `.body_new`'s are not. */
const DARK_BODY = {
  color: "common.white",
  opacity: 0.8,
  textAlign: "center",
  fontSize: 16,
  lineHeight: "24px",
  fontWeight: 300,
  width: "40ch",
  // The band's line holds one size at every width, where body copy steps down.
  [TINY]: { fontSize: 16, lineHeight: "24px", width: 1 },
};

/** `.body_new.is-white-small`. */
const ALT_BODY = {
  color: "common.white",
  opacity: 0.6,
  fontSize: 15,
  lineHeight: "22.5px",
  // Restated at each step: this line holds 15 where body copy steps down to it.
  [MID]: { fontSize: 15, lineHeight: "22.5px" },
  [TINY]: { fontSize: 15, lineHeight: "21px" },
};

/** The mint the published band rules and labels its second control in. */
const ALT_SECONDARY = "#67EED3";

/** `.buttons-header_list`: a full-width row that stacks below the card step. */
const ALT_CTAS = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  width: 1,
  // `.button_secondary.is-dark-option`: every control after the first is pale
  // mint, not a white outline.
  "& > :nth-child(n + 2)": {
    borderColor: ALT_SECONDARY,
    color: ALT_SECONDARY,
    "&:hover": {
      borderColor: ALT_SECONDARY,
      backgroundColor: "rgba(26, 179, 148, 0.3)",
    },
  },
  [MID]: { flexDirection: "column", alignItems: "stretch" },
  [TINY]: { gap: "12px" },
};

function BandControls({
  ctas,
  sx,
  variant,
}: {
  ctas: ReadonlyArray<LandingCta>;
  sx: SxProps<Theme>;
  /** The second control's fill; the first is always the filled one. */
  variant?: "onDark" | "secondary";
}) {
  return (
    <Box sx={sx}>
      {ctas.map((cta, index) => (
        <LandingButton
          key={index}
          cta={cta}
          variant={index === 0 ? "primary" : (variant ?? "secondary")}
        />
      ))}
    </Box>
  );
}

/** `sx` accepts an array of styles but not an array inside one, so flatten first. */
function asList(sx: SxProps<Theme>) {
  return Array.isArray(sx) ? sx : [sx];
}

/** The heading pair a dark band opens on; each band states its own ladder. */
function DarkHeading({
  block,
  title,
  body,
}: {
  block: CtaBand;
  title: SxProps<Theme>;
  body: SxProps<Theme>;
}) {
  return (
    <>
      {block.title ? (
        <Typography component="h2" sx={[HEADING_2, ...asList(title)]}>
          <Lines text={block.title} />
        </Typography>
      ) : null}
      {block.body ? (
        <Typography sx={[BODY, ...asList(body)]}>{block.body}</Typography>
      ) : null}
    </>
  );
}

/** What a band closes on: the dotted line, then the row of ticks. */
function BandFoot({
  block,
  iconSize,
  onDark,
  quiet,
}: {
  block: CtaBand;
  iconSize: number;
  onDark?: boolean;
  /** The register well's quieter line. */
  quiet?: boolean;
}) {
  return (
    <>
      {block.reassurances?.length ? (
        <ReassuranceRow items={block.reassurances} quiet={quiet} />
      ) : null}
      {block.checks?.length ? (
        <CheckRow checks={block.checks} iconSize={iconSize} onDark={onDark} />
      ) : null}
    </>
  );
}

/**
 * The six animations the navy band floats over its ground, each a Lottie the
 * published page plays on a loop, placed as its own class places it: two star
 * fields, three planets and the cosmonaut. Sizes and insets are the published
 * ones, `em` and `vw` included, since that is how they scale between steps.
 */
const ART = "/marketing/webflow";
const BAND_ART: ReadonlyArray<{ path: string; sx: SxProps<Theme> }> = [
  {
    path: `${ART}/64b7ea7b1f9ec7723d998a8b_BG_2.json`,
    // `.cta_stars_right`
    sx: {
      opacity: 0.35,
      width: "656px",
      top: "-18%",
      left: "-18%",
      [NOT_WIDE]: { width: "50vw", height: "50vw" },
      [MID]: {
        top: "auto",
        bottom: "-14%",
        left: "-21%",
        width: "60vw",
        maxWidth: "640px",
        height: "60vw",
      },
      [TINY]: { width: "80vw", height: "80vw", bottom: "-21%", left: "-33%" },
    },
  },
  {
    path: `${ART}/64b7e846856eed9e41a35efe_third_planet.json`,
    // `.cta_third_planet`
    sx: {
      width: "200px",
      bottom: "-9%",
      left: "8%",
      [NOT_WIDE]: { width: "17vw", bottom: "-1%" },
      [MID]: { bottom: "2%" },
    },
  },
  {
    path: `${ART}/64b7e84616f5744348611660_green_planet.json`,
    // `.cta_earht_green`
    sx: {
      zIndex: 2,
      width: "64px",
      height: "64px",
      top: "18%",
      right: "8%",
      [NOT_WIDE]: { width: "48px", height: "48px" },
      [MID]: { top: "auto", bottom: "15%", right: "48%" },
      [TINY]: { width: "48px", height: "48px", bottom: "5%", right: "48%" },
    },
  },
  {
    path: `${ART}/64b7e5000f1746238795f5ec_cosmonaut.json`,
    // `.cta_cosmonaut`
    sx: {
      zIndex: 10,
      width: "21em",
      bottom: "-7%",
      right: "5%",
      [NOT_WIDE]: { width: "33vw", right: "-2%" },
      [MID]: { width: "37vw", minWidth: "256px", bottom: "-12%", right: "0%" },
      [TINY]: { bottom: "-6%", right: "-8%" },
    },
  },
  {
    path: `${ART}/64b7e8465e9e67ee4fcdfb71_BG.json`,
    // `.cta_stars_left`
    sx: {
      zIndex: 2,
      opacity: 0.6,
      width: "20em",
      top: "-9%",
      right: "-4%",
      transform: "scale(1.4) rotate(-58deg)",
      [NOT_WIDE]: { width: "40vw", height: "40vw", top: "-15%", right: "-10%" },
      [MID]: {
        top: "auto",
        bottom: "-31%",
        right: "-12%",
        width: "50vw",
        maxWidth: "300px",
        height: "50vw",
      },
      [TINY]: { bottom: "-14%" },
    },
  },
  {
    path: `${ART}/64b7e84660f97b46d63799e1_purple_planet.json`,
    // `.cta_earht_purple`
    sx: {
      zIndex: 2,
      width: "40px",
      height: "40px",
      top: "28%",
      right: "14%",
      [NOT_WIDE]: { width: "32px", height: "32px" },
      [MID]: { width: "25.6px", height: "25.6px", top: "73%", right: "60%" },
      [TINY]: { width: "19.2px", height: "19.2px", top: "75%", right: "52%" },
    },
  },
];

function BandArt() {
  return (
    <>
      {BAND_ART.map((art) => (
        <Box
          key={art.path}
          aria-hidden
          sx={[
            { position: "absolute", pointerEvents: "none" },
            ...asList(art.sx),
          ]}
        >
          <MarketingLottie
            path={art.path}
            play
            loop
            style={{ width: "100%", height: "100%" }}
          />
        </Box>
      ))}
    </>
  );
}

/**
 * `.cta_wrap`: the navy band, its control inside the heading stack, over the
 * art. `.cta-small_title_wrap` stands over the art below the wide step, where
 * the cosmonaut would otherwise cross the copy.
 */
function DarkBand({ block }: { block: CtaBand }) {
  return (
    <LandingSection padding="both">
      <Box sx={[BAND_FRAME, DARK_BAND]}>
        <BandArt />
        <Box
          sx={[DARK_INFO, { [NOT_WIDE]: { position: "relative", zIndex: 20 } }]}
        >
          <DarkHeading
            block={block}
            title={block.heroTitle ? ALT_TITLE_DIV : DARK_TITLE}
            body={DARK_BODY}
          />
          {block.ctas?.length ? (
            <BandControls
              ctas={block.ctas}
              sx={FIXED_BUTTON_ROW}
              variant="onDark"
            />
          ) : null}
        </Box>
        <BandFoot block={block} iconSize={16} onDark />
      </Box>
    </LandingSection>
  );
}

/** `.alter-cta_wrap`: the same ground, with its controls on a row of their own. */
function AltBand({ block }: { block: CtaBand }) {
  return (
    <LandingSection padding="both">
      <Box sx={[BAND_FRAME, ALT_BAND]}>
        <Box sx={ALT_INFO}>
          <DarkHeading block={block} title={ALT_TITLE} body={ALT_BODY} />
        </Box>
        {block.ctas?.length ? (
          <BandControls ctas={block.ctas} sx={ALT_CTAS} variant="onDark" />
        ) : null}
        <BandFoot block={block} iconSize={16} onDark />
      </Box>
    </LandingSection>
  );
}

/** `.y-p_cta_box`: the pastel box, its `is_fixed` control under the words. */
function GradientBand({ block }: { block: CtaBand }) {
  return (
    <LandingSection padding="none" sx={SHALLOW_FOOT}>
      <Box sx={[BAND_FRAME, GRADIENT_BAND]}>
        <PlainBandHeading
          title={block.title}
          body={block.body}
          bodyStyle={BODY}
          sx={{ maxWidth: "500px" }}
        />
        {block.ctas?.length ? (
          <BandControls ctas={block.ctas} sx={FIXED_BUTTON_ROW} />
        ) : null}
        <BandFoot block={block} iconSize={24} />
      </Box>
    </LandingSection>
  );
}

/**
 * `.table_button_wrap` after the panels in the rotating page's grey well: the
 * well sits 24 under them, and the cloneable's 100 closes under it.
 */
const TAIL_LEAD = { pt: "24px" } satisfies SxProps<Theme>;

function PlainWell({ block }: { block: CtaBand }) {
  const tail = block.pad === "tail";
  return (
    <LandingSection
      padding="bottom"
      ground={tail ? "grey" : undefined}
      sx={tail ? TAIL_LEAD : undefined}
    >
      <Box sx={[BAND_FRAME, PLAIN_WELL]}>
        <PlainBandHeading title={block.title} body={block.body} />
        {block.ctas?.length ? (
          <BandControls
            ctas={block.ctas}
            sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
            variant="secondary"
          />
        ) : null}
        <BandFoot block={block} iconSize={24} quiet />
      </Box>
    </LandingSection>
  );
}

export function CtaBandBlock({ block }: { block: CtaBand }) {
  if (block.ctaBand === "plain") {
    return <PlainWell block={block} />;
  }
  if (block.ctaBand === "alternative") {
    return <AltBand block={block} />;
  }
  if (block.ctaBand === "pastel") {
    return <GradientBand block={block} />;
  }
  return <DarkBand block={block} />;
}
